import { Injectable } from '@angular/core';
import {combineLatest, filter, first, Observable, throwError} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {catchError, map, mergeMap, switchMap} from "rxjs/operators";
import {AuthService} from "./auth.service";
import * as BillingActions from "../state/billing/billing.actions";
import * as BillingSelectors from "../state/billing/billing.selectors";
import {Store} from "@ngrx/store";
import {PopupService} from "./popup.service";

interface BillingPortalResponse {
  data: {
    url: string
  },
  metadata: {
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {

  user: any;
  subscription: any;

  private apiUrl = environment.api.url;
  token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private store: Store,
    private popupService: PopupService,
  ) {}

  /*
  billingPortal(user_id: number): Observable<{ data: any }> {
    const url = `${this.apiUrl}/users/billing_portal/get_url`;
    const requestBody = { user_id };

    // Start the stream with the token retrieval
    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` // Use the token here
          })
        };

        return this.http.post<{ data: any, metadata: { message: string } }>(
          url,
          requestBody,
          { ...httpOptions, observe: 'response' } // Spread httpOptions and set observe to 'response'
        );
      }),
      map(response => {
        if (response.status === 200 && response.body && response.body.data) {
          return response.body.data
        } else {
          throw new Error(`Billing portal failed: ${response.body && response.body.metadata ? response.body.metadata.message : 'No data received'}`);
        }
      }),
      catchError(error => {
        console.error('Billing portal request failed:', error);
        throw error;
      })
    );
  }

  openBillingPortal () {
    // console.log('[billing.service.ts] openBillingPortal')

    this.authService.authUser().subscribe(user => {
      this.user = user;

      const userId = user.id
      this.store.dispatch(BillingActions.billingPortal({ userId }));
    });

    this.store.select(BillingSelectors.selectBillingPortalUrl)
      .subscribe(url => {
      if (url) {
        console.log('Billing portal, redirecting to URL:', url);
        window.open(url, '_blank');
      }
    });

    this.store.select(BillingSelectors.selectBillingPortalError).subscribe(error => {
      if (error) {
        console.error('Billing portal error:', error);
        this.popupService.notify(error.error.message, 'Billing portal Error', 'error');
      }
    });

  }
  */

  billingPortal(user_id: number): Observable<{ data: any }> {
    const url = `${this.apiUrl}/users/billing_portal/get_url`;
    const requestBody = { user_id };

    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          })
        };

        return this.http.post<{ data: any, metadata: { message: string } }>(
          url,
          requestBody,
          { ...httpOptions, observe: 'response' }
        );
      }),
      map(response => {
        if (response.status === 200 && response.body && response.body.data) {
          return response.body.data;
        } else {
          throw new Error(`Billing portal failed: ${response.body && response.body.metadata ? response.body.metadata.message : 'No data received'}`);
        }
      }),
      catchError(error => {
        console.error('Billing portal request failed:', error);
        throw error;
      })
    );
  }

  openBillingPortal(): void {
    this.authService.authUser().pipe(
      first(),
      mergeMap(user => {
        this.user = user;
        const userId = user.id;
        this.store.dispatch(BillingActions.billingPortal({ userId }));

        // Wait for the URL to be updated in the store
        return this.store.select(BillingSelectors.selectBillingPortalUrl).pipe(
          filter(url => !!url), // Wait until the URL is not null or undefined
          first() // Take the first emitted value that matches the filter
        );
      })
    ).subscribe({
      next: (url) => {
        console.log('Billing portal, redirecting to URL:', url);
        window.open(url, '_blank');
      },
      error: (error) => {
        this.store.select(BillingSelectors.selectBillingPortalError).pipe(first()).subscribe(error => {
          if (error) {
            console.error('Billing portal error:', error);
            this.popupService.notify(error.error.message, 'Billing portal Error', 'error');
          }
        });
      }
    });
  }


  fetchBillings(userId: number, page: number): Observable<any> {

    const url = `${this.apiUrl}/billables?filter[user_id]=${userId}&page=${page}`;

    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }),
          observe: 'response' as 'response'
        };

        return this.http.get<{ data: any, metadata: { message: string } }>(
          url,
          httpOptions
        );
      }),
      map(response => {
        if (response.status === 200 && response.body && response.body.data) {
          return response.body.data;
        } else {
          throw new Error(`Fetch billings failed: ${response.body?.metadata?.message || 'No data received'}`);
        }
      }),
      catchError(error => {
        console.error('Fetch billings request failed:', error);
        return throwError(() => new Error('Fetch billings request failed'));
      })
    );
  }

  fetchInvoice(invoiceId: number): Observable<any> {
    const url = `${this.apiUrl}/billables/${invoiceId}`;

    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }),
          observe: 'response' as 'response'
        };

        return this.http.get<{ data: any, metadata: { message: string } }>(
          url,
          httpOptions
        );
      }),
      map(response => {
        if (response.status === 200 && response.body && response.body.data) {
          return response.body.data;
        } else {
          throw new Error(`Fetch invoice failed: ${response.body?.metadata?.message || 'No data received'}`);
        }
      }),
      catchError(error => {
        console.error('Fetch invoice request failed:', error);
        return throwError(() => new Error('Fetch invoice request failed'));
      })
    );
  }

}
